import type { Tone } from '../../ui/v2';

/**
 * The store onboarding checklist, as served by
 * `GET /api/store/onboarding-checklist/`. The backend decides which items
 * apply and what state each is in; this module only labels and groups them.
 */

export type ItemStatus = 'done' | 'todo' | 'waiting' | 'recommended' | 'locked';
export type StoreStatus = 'not_started' | 'in_setup' | 'ready' | 'live';
export type Phase = 'setup' | 'test' | 'go_live';

export interface ChecklistItem {
  key: string;
  title: string;
  phase: Phase;
  required: boolean;
  status: ItemStatus;
  detail: string;
  store_confirmable: boolean;
  state: '' | 'confirmed' | 'done';
  note: string;
  updated_at: string | null;
}

/**
 * While the checklist is turned off, stores get only `{ enabled: false }`:
 * check `enabled` before reading anything else.
 */
export interface Checklist {
  enabled: boolean;
  store_id: number;
  store_name: string;
  status: StoreStatus;
  agent_number: string;
  main_number: string;
  go_live_confirmed_at: string | null;
  live_at: string | null;
  live_method: '' | 'marked' | 'call_volume' | 'forwarded' | 'caller_match';
  counts: { done: number; total: number; todo: number; waiting: number };
  next_step: { key: string; title: string } | null;
  items: ChecklistItem[];
  forwarding: Forwarding;
  /** Owners, managers and platform admins can confirm items. */
  can_confirm: boolean;
  /** Owners and platform admins: it decides where payments go. */
  can_connect_stripe: boolean;
  /** Platform admins only. */
  internal_issues?: string[];
  test_feedback?: TestFeedback[];
}

/** The Go live screen's forwarding check. */
export interface Forwarding {
  country: 'US' | 'CA';
  test_caller_number: string;
  check_started_at: string | null;
  check_expires_at: string | null;
  checking: boolean;
  /** A forwarded call came from this number, not the saved main number. */
  pending_forwarded_from: string;
  /**
   * A call came from the test phone, but nothing shows it was forwarded:
   * the store is asked whether it dialled its main number.
   */
  pending_test_call: boolean;
  forwarded_from: string;
  live_call_id: string;
}

/** What the Go live screen polls: cheap, and never runs internal checks. */
export interface ForwardingStatus {
  live_at: string | null;
  live_method: Checklist['live_method'];
  go_live_confirmed_at: string | null;
  forwarding: Forwarding;
}

export interface TestFeedback {
  id: number;
  message: string;
  call_id: string;
  created_at: string;
}

export interface TestPrompt {
  say: string;
  expect: string;
}

export interface TestCall {
  call_id: string;
  from_number: string;
  start_time: string | null;
  duration_ms: number | null;
  ended: boolean;
  turns: Array<{ role: 'agent' | 'user'; text: string }>;
  more_turns: number;
  summary: string;
}

/** One of the store's documents, in the Documents step. */
export interface StoreDocument {
  id: number;
  name: string;
  size_bytes: number;
  uploaded_at: string;
  /** Uploaded as the FAQs, which the step requires. */
  is_faq: boolean;
}

export interface FaqCheck {
  status: 'checking' | 'rejected';
  name: string;
  size_bytes: number;
  reason: string;
}

/** The Documents step, from `GET /api/store/onboarding-checklist/documents/`. */
export interface DocumentsPage {
  documents: StoreDocument[];
  /** The FAQ page, the other way to give the FAQs; '' when there's none. */
  faq_url: string;
  /**
   * An FAQ file being checked for at least one question with its answer, or
   * the last one turned down (with why). Null when there's nothing to show.
   */
  faq_check: FaqCheck | null;
  /** Owners and managers upload and remove. */
  can_upload: boolean;
}

/** Test your agent, from `GET /api/store/onboarding-checklist/test/`. */
export interface TestPage {
  store_name: string;
  agent_number: string;
  test_status: ItemStatus;
  prompts: TestPrompt[];
  calls: TestCall[];
  feedback: TestFeedback[];
  can_confirm: boolean;
}

/** One row of the admin onboarding table, from the onboarding status list. */
export interface ChecklistSummary {
  store_id: number;
  store_name: string;
  status: StoreStatus;
  counts: Checklist['counts'];
  next_step: Checklist['next_step'];
  waiting_on_us: string[];
  live_at: string | null;
}

/** The summary row for a checklist the admin dialog just changed. */
export const checklistSummary = (checklist: Checklist): ChecklistSummary => ({
  store_id: checklist.store_id,
  store_name: checklist.store_name,
  status: checklist.status,
  counts: checklist.counts,
  next_step: checklist.next_step,
  waiting_on_us: checklist.items
    .filter((item) => item.status === 'waiting')
    .map((item) => item.title),
  live_at: checklist.live_at,
});

export const PHASES: Array<{ phase: Phase; label: string }> = [
  { phase: 'setup', label: 'Set up' },
  { phase: 'test', label: 'Test' },
  { phase: 'go_live', label: 'Go live' },
];

export type PhaseState = 'done' | 'active' | 'upcoming';

/** One phase of Getting started, as the page shows it. */
export interface PhaseView {
  phase: Phase;
  /** 1-based place in the sequence. */
  number: number;
  label: string;
  items: ChecklistItem[];
  progress: { done: number; total: number };
  state: PhaseState;
  /** Open items that don't block the phase (recommended). */
  recommended: number;
  /** Required items waiting on Ask Arthur. */
  waiting: number;
  /** Why an upcoming phase is still closed. */
  unlockHint: string;
}

/**
 * The phases in order, each open, done or still ahead. One phase is open
 * at a time: the first whose required items aren't all done. The next
 * phase opens once this one has nothing left for the store to do — done,
 * or only waiting on Ask Arthur — so a wait never leaves the store with
 * nothing to work on. A phase the server locks (go live until everything
 * else, including our part, is done) stays ahead until it unlocks.
 */
export const phaseViews = (checklist: Checklist): PhaseView[] => {
  const live = checklist.status === 'live';
  const since = new Set(stepsSinceLive(checklist).map((item) => item.key));
  let reachable = true;
  const views: PhaseView[] = [];
  PHASES.forEach(({ phase, label }) => {
    const items = itemsInPhase(checklist, phase);
    if (!items.length) return;
    // Once live, only steps for something added since still count.
    const required = items.filter(
      (item) => item.required && (!live || since.has(item.key)),
    );
    const progress = phaseProgress(items);
    const done = required.every((item) => item.status === 'done');
    const serverLocked =
      required.length > 0 && required.every((item) => item.status === 'locked');
    const state: PhaseState = done
      ? 'done'
      : reachable && !serverLocked
        ? 'active'
        : 'upcoming';
    const number = views.length + 1;
    const lockedDetail = required.find(
      (item) => item.status === 'locked' && item.detail,
    )?.detail;
    views.push({
      phase,
      number,
      label,
      items,
      progress,
      state,
      recommended: items.filter((item) => item.status === 'recommended').length,
      waiting: required.filter((item) => item.status === 'waiting').length,
      unlockHint:
        state === 'upcoming'
          ? serverLocked && lockedDetail
            ? lockedDetail
            : `Unlocks when step ${number - 1} is done`
          : '',
    });
    reachable =
      reachable &&
      state !== 'upcoming' &&
      !required.some((item) => item.status === 'todo');
  });
  return views;
};

export const STORE_STATUS_LABEL: Record<StoreStatus, string> = {
  not_started: 'Not started',
  in_setup: 'In setup',
  ready: 'Ready to go live',
  live: 'Live',
};

export const storeStatusTone = (status: StoreStatus): Tone => {
  if (status === 'live') return 'success';
  if (status === 'ready') return 'accent';
  return 'neutral';
};

export const ITEM_STATUS_LABEL: Record<ItemStatus, string> = {
  done: 'Done',
  todo: 'To do',
  waiting: 'Waiting on Ask Arthur',
  recommended: 'Recommended',
  locked: 'Last step',
};

export const itemStatusTone = (status: ItemStatus): Tone => {
  if (status === 'done') return 'success';
  if (status === 'waiting' || status === 'recommended') return 'warning';
  return 'neutral';
};

/** How the store's main number reaches its phone company. */
export type LineType = 'landline' | 'mobile' | 'voip';

/** The 10-digit national number, without +1 or punctuation. */
export const nationalDigits = (number: string) => {
  const digits = number.replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits;
};

/**
 * Star codes to forward every call, shared by US and Canadian carriers.
 * VoIP systems have none: forwarding is set in the provider's portal.
 */
export const forwardingCodes = (lineType: LineType, agentNumber: string) => {
  const digits = nationalDigits(agentNumber);
  if (lineType === 'landline') return { on: `*72${digits}`, off: '*73' };
  if (lineType === 'mobile') return { on: `**21*${digits}#`, off: '##21#' };
  return null;
};

export const itemsInPhase = (checklist: Checklist, phase: Phase) =>
  checklist.items.filter((item) => item.phase === phase);

export const phaseProgress = (items: ChecklistItem[]) => {
  const required = items.filter((item) => item.required);
  return {
    done: required.filter((item) => item.status === 'done').length,
    total: required.length,
  };
};

/** Items still waiting on an answer that don't block going live. */
export const recommendedItems = (checklist: Checklist) =>
  checklist.items.filter((item) => item.status === 'recommended');

/**
 * Once live, the steps still open are for something the store took on
 * afterwards, like phone orders. Testing and going live are behind it.
 */
export const stepsSinceLive = (checklist: Checklist) =>
  checklist.status === 'live'
    ? checklist.items.filter(
        (item) =>
          item.required &&
          item.status !== 'done' &&
          item.phase !== 'test' &&
          item.key !== 'go_live',
      )
    : [];

export const stepsLeft = (checklist: Checklist) =>
  checklist.status === 'live'
    ? stepsSinceLive(checklist).length
    : checklist.counts.total - checklist.counts.done;

/** What's left for a live store, in a line. */
export const sinceLiveLine = (left: number) =>
  left
    ? `${left} step${left === 1 ? '' : 's'} for what you’ve added since going live`
    : 'Your agent is answering calls.';

/** The step to start on: once live, the first one added since. */
export const nextStep = (checklist: Checklist) => {
  if (checklist.status !== 'live') return checklist.next_step;
  const next = stepsSinceLive(checklist).find((item) => item.status === 'todo');
  return next ? { key: next.key, title: next.title } : null;
};

/**
 * Getting started is shown only while there are steps left to complete:
 * all of setup until the store is live, then whatever it added since;
 * only when enabled.
 */
export const showsGettingStarted = (checklist: Checklist | undefined) =>
  Boolean(checklist?.enabled && checklist.counts && stepsLeft(checklist) > 0);

/**
 * Until a store first goes live, the nav keeps to what setup needs. After
 * that it never shrinks again, even when Getting started comes back.
 */
export const limitsNav = (checklist: Checklist | undefined) =>
  Boolean(checklist?.enabled && checklist.status !== 'live');

export const summaryLine = (counts: Checklist['counts']) => {
  // "0 to do" says nothing; when only our part is left, that's the line.
  const parts = counts.todo || !counts.waiting ? [`${counts.todo} to do`] : [];
  if (counts.waiting) parts.push(`${counts.waiting} waiting on Ask Arthur`);
  return parts.join(' · ');
};
